/*
 * Copyright (c) 2020 CalAmp Corp.  All Rights Reserved
 */
import React, {
  MouseEventHandler,
  ReactNode,
  useCallback
} from 'react';
import {makeStyles} from '@material-ui/core/styles';
import isNil from 'lodash.isnil';
import {translate} from '@ion/core/src/locale/locale';
import useMedia from '@ion/dls/src/hooks/useMedia';
import {AcknowledgeIcon} from '../../src/icons/AcknowledgeIcon';
import {MapEditIcon} from '../../src/icons/MapEditIcon';
import {MaterialMoreVertIcon} from '../../src/icons/MaterialMoreVertIcon';
import {RmaIcon} from '../../src/icons/RmaIcon';
import {SystemDeleteIcon} from '../../src/icons/SystemDeleteIcon';
import {ViewRecordIcon} from '../../src/icons/ViewRecordIcon';
import {dlsColor} from '../../src/theme/theme';
import HeaderButton from './HeaderButton';
import {BulkHeader} from './IonGrid';
import SelectionHeaderButtons from './SelectionHeaderButtons';
import SelectionHeaderContainer from './SelectionHeaderContainer';
import SelectionHeaderCount from './SelectionHeaderCount';

// defines the selections header prop definitions ...
export interface SelectionsHeaderProps extends BulkHeader {
  onBulkEdit?: (assets: any) => void;
  onBulkDelete?: (assets: any) => void;
  onBulkView?: (assets: any) => void;
  onBulkAction?: (value: any) => void;
  onBulkUpdate?: (value: any) => void;
  actionMenuItems?: ReactNode;
  onBulkAcknowledge?: (events: any) => void;
  selectedRows: Array<any> | [];
  omitCTAs?: boolean;
  showBulkEdit?: boolean;
  disableBulkEdit?: boolean;
  disableBulkDelete?: boolean;
  customSelectionHeaderButton?: any;
  count: number;
  disableBulkActions?: boolean;
  showDisabledBulkTooltip?: boolean;
  bulkDeleteLabel?: string;
}

const useStyles = makeStyles(() => ({
  customPointerClass : {
    '& .Mui-disabled' : {
      pointerEvents : ({showDisabledBulkTooltip}: {showDisabledBulkTooltip: boolean}) => (
        showDisabledBulkTooltip ? 'auto' : 'none'
      )
    }
  }
}));

/**
 * @SelectionsHeader
 *
 * Defines the SelectionsHeader component. This component displays the selected item count
 * on top of the grid with options to bulk edit/delete the selected assets.
 * This component is displayed only when multiple items are selected.
 *
 * ```typescript
 *
 *   <SelectionsHeader onBulkEdit={onBulkEdit} onBulkDelete={onBulkDelete} gridApi={gridApi} />
 *
 * ```
 */
export const SelectionsHeader = ({
  selectedRows,
  onBulkActionsClick,
  onBulkView,
  onBulkEdit,
  onBulkDelete,
  onBulkAction,
  onBulkUpdate,
  actionMenuItems,
  onBulkAcknowledge,
  customSelectionHeaderButton,
  bulkDeleteLabel,
  count = selectedRows?.length,
  omitCTAs = false,
  showBulkEdit = true,
  disableBulkActions = false,
  disableBulkDelete = false,
  disableBulkEdit = false,
  showDisabledBulkTooltip = false
}: SelectionsHeaderProps) => {
  const classes          = useStyles({showDisabledBulkTooltip});
  const iconColor        = dlsColor('white');
  const {isPhone}        = useMedia();
  const selectionsHeader = translate('grids:selectionsHeader', {returnObjects: true});

  const onActionWithSelectedRows = useCallback((callback): MouseEventHandler =>
    () => callback?.(selectedRows),
  [selectedRows]);

  if (onBulkActionsClick) {
    return (
      <SelectionHeaderContainer show>
        <SelectionHeaderCount count={count} message={'grids:selectionsHeader.result'} />
        {!omitCTAs && (
          <SelectionHeaderButtons>
            <div className={classes.customPointerClass}>
              <HeaderButton onClick={showDisabledBulkTooltip ? undefined : onBulkActionsClick}
                disabled={disableBulkActions}
                showDisabledBulkTooltip={showDisabledBulkTooltip}
                icon={<MaterialMoreVertIcon color={iconColor} />}
                label={selectionsHeader.bulkActions} />
            </div>
            {onBulkView && (
              <HeaderButton onClick={onActionWithSelectedRows(onBulkView)}
                icon={<ViewRecordIcon color={iconColor} />}
                label={!isPhone ? selectionsHeader.view : ''} />
            )}
            {onBulkDelete && (
              <HeaderButton disabled={!onBulkDelete || disableBulkDelete}
                onClick={onActionWithSelectedRows(onBulkDelete)}
                icon={bulkDeleteLabel
                  ? <RmaIcon color={iconColor} />
                  : <SystemDeleteIcon color={iconColor} />
                }
                label={!isPhone ? (bulkDeleteLabel || selectionsHeader.delete) : ''} />
            )}
          </SelectionHeaderButtons>
        )}
      </SelectionHeaderContainer>
    );
  }

  return (
    <SelectionHeaderContainer show>
      <SelectionHeaderCount count={count} message={'grids:selectionsHeader.result'} />
      {!omitCTAs && (
        <SelectionHeaderButtons>
          {actionMenuItems || ''}
          {onBulkAction && (
            <HeaderButton onClick={onBulkAction}
              label={translate('toolbox-modbus:grid.options.slaveId.label')} />
          )}
          {onBulkUpdate && (
            <HeaderButton onClick={onBulkUpdate}
              label={translate('toolbox-modbus:grid.options.functionCode.label')} />
          )}
          {showBulkEdit && isNil(customSelectionHeaderButton) && onBulkEdit && (
            <HeaderButton disabled={disableBulkEdit}
              onClick={onActionWithSelectedRows(onBulkEdit)}
              icon={<MapEditIcon color={iconColor} />}
              label={!isPhone ? translate('grids:selectionsHeader.edit') : ''} />
          )}
          {isNil(customSelectionHeaderButton) && onBulkDelete && (
            <HeaderButton disabled={disableBulkDelete}
              onClick={onActionWithSelectedRows(onBulkDelete)}
              icon={bulkDeleteLabel
                ? <RmaIcon color={iconColor} />
                : <SystemDeleteIcon color={iconColor} />
              }
              label={!isPhone ? (bulkDeleteLabel || translate('grids:selectionsHeader.delete')) : ''} />
          )}
          {customSelectionHeaderButton?.()}
          {onBulkAcknowledge && (
            <HeaderButton onClick={onActionWithSelectedRows(onBulkAcknowledge)}
              icon={<AcknowledgeIcon color={iconColor} />}
              label={!isPhone ? translate('grids:selectionsHeader.acknowledge') : ''} />
          )}
        </SelectionHeaderButtons>
      )}
    </SelectionHeaderContainer>
  );
};

export default SelectionsHeader;
